import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { foutData } from '../utils/apiFout'

const DAGEN = ['Ma', 'Di', 'Wo', 'Do', 'Vr']
const STATUS_KLEUR = {
  gepland: '#4A90D9', gestart: '#2D6A4F', afgerond: '#9CA3AF',
  geblokkeerd: '#B02020', orderverwerking: '#E8830A',
  zagen: '#7B68EE', afplakken: '#20B2AA', cnc: '#A78BFA',
  afwerking: '#FB923C', klaar_voor_verzending: '#16A34A',
}

export default function GanttDragDrop() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [weekOffset, setWeekOffset] = useState(0)
  const [slepen, setSlepen] = useState(null)
  const [doel, setDoel] = useState(null)
  const [bevestig, setBevestig] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['week-gantt', weekOffset],
    queryFn: () => api.get('/ai-planning/week-gantt', { params: { week_offset: weekOffset } }).then(r => r.data),
    staleTime: 30000,
  })

  // Drag handlers
  function onDragStart(slot, resourceId, dagIdx) {
    setSlepen({ slot, bronResourceId: resourceId, bronDagIdx: dagIdx })
  }
  function onDragOver(e, resourceId, dagIdx) {
    e.preventDefault()
    setDoel({ resourceId, dagIdx })
  }
  function onDragLeave() { setDoel(null) }
  function onDrop(e, resourceId, dagIdx) {
    e.preventDefault()
    if (!slepen) return
    if (slepen.bronResourceId === resourceId && slepen.bronDagIdx === dagIdx) {
      setSlepen(null); setDoel(null); return
    }
    setBevestig({ slot: slepen.slot, nieuweResourceId: resourceId, nieuweDagIdx: dagIdx,
      resourceNaam: (data?.resources || []).find(r => r.id === resourceId)?.naam || '?' })
    setSlepen(null); setDoel(null)
  }
  async function bevestigVerplaats() {
    if (!bevestig) return
    try {
      await api.patch(`/orders/${bevestig.slot.id}/herplan`, {
        nieuwe_datum: data?.maandag || new Date().toISOString(),
        machine_id: bevestig.nieuweResourceId,
        force: false,
      })
      qc.invalidateQueries(['week-gantt'])
    } catch (err) {
      // Het object staat in detail_data (detail is tekst): eerst de boodschap daaruit.
      const detail = err.response?.data?.detail
      alert(foutData(err)?.boodschap || (typeof detail === 'string' && detail) || 'Fout bij verplaatsen')
    }
    setBevestig(null)
  }

  if (isLoading) return <div className="p-6 text-gray-500">Planning laden...</div>
  if (error) return <div className="p-6 text-red-600">Fout: {error.message}</div>

  const resources = data?.resources || []
  const slots = data?.slots || []
  const slotMap = {}
  slots.forEach(s => { const k = `${s.resource_id}_${s.dag_index}`; if (!slotMap[k]) slotMap[k] = []; slotMap[k].push(s) })

  return (
    <div className="p-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <h2 className="m-0 text-[#1A3A5C] font-bold text-lg">📅 Productiekalender</h2>
        <button onClick={() => setWeekOffset(w => w - 1)} className="px-3 py-1 border rounded text-sm cursor-pointer hover:bg-gray-100">◀ Vorige</button>
        <button onClick={() => setWeekOffset(0)} className="px-3 py-1 bg-[#1A3A5C] text-white rounded text-sm cursor-pointer">Vandaag</button>
        <button onClick={() => setWeekOffset(w => w + 1)} className="px-3 py-1 border rounded text-sm cursor-pointer hover:bg-gray-100">Volgende ▶</button>
        <span className="text-sm font-bold text-gray-600">{data?.week_label || ''}</span>
        <span className="text-xs text-gray-400 ml-auto">Sleep blokken om te verplaatsen</span>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full border-collapse" style={{ minWidth: 720 }}>
          <thead>
            <tr className="bg-[#1A3A5C] text-white">
              <th className="px-3 py-2 text-left text-xs font-bold" style={{ width: 150 }}>Resource</th>
              {DAGEN.map(d => <th key={d} className="px-3 py-2 text-center text-xs font-bold" style={{ width: 130 }}>{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-400">Geen machines geconfigureerd</td></tr>
            )}
            {resources.map((res, i) => (
              <tr key={res.id} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="px-3 py-2 border-r-2 border-gray-200">
                  <div className="font-bold text-sm text-[#1A3A5C]">{res.naam}</div>
                  <div className="text-xs text-gray-400">{res.type}</div>
                  {(() => {
                    const ob = (data?.overbelasting || []).filter(o => o.resource_id === res.id)
                    return ob.length > 0 ? (
                      <div className="text-xs text-red-600 font-bold mt-0.5">
                        ⚠️ {ob.map(o => `${DAGEN[o.dag_index]}:${o.bezetting_pct}%`).join(' ')}
                      </div>
                    ) : null
                  })()}
                </td>
                {DAGEN.map((_, dagIdx) => {
                  const dagSlots = slotMap[`${res.id}_${dagIdx}`] || []
                  const isDoelCel = doel?.resourceId === res.id && doel?.dagIdx === dagIdx
                  return (
                    <td key={dagIdx}
                      onDragOver={(e) => onDragOver(e, res.id, dagIdx)}
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDrop(e, res.id, dagIdx)}
                      className="px-1 py-1 border-r border-gray-100 align-top transition-colors"
                      style={{ minHeight: 50, background: isDoelCel ? '#DBEAFE' : 'transparent' }}>
                      {dagSlots.map(slot => (
                        <div key={slot.id}
                          draggable
                          onDragStart={() => onDragStart(slot, res.id, dagIdx)}
                          title={`${slot.order_nummer}\n${slot.start_uur} – ${slot.eind_uur}\nStatus: ${slot.status}\n\nSleep om te verplaatsen`}
                          className="rounded px-1.5 py-1 mb-1 text-white cursor-grab active:cursor-grabbing select-none"
                          style={{
                            background: slot.heeft_conflict ? '#B02020' : (STATUS_KLEUR[slot.status] || '#4A90D9'),
                            fontSize: 11,
                            opacity: slepen?.slot?.id === slot.id ? 0.4 : 1,
                            transition: 'opacity 0.15s',
                          }}>
                          <div className="font-bold truncate">{slot.order_nummer || slot.klantnaam || '?'}</div>
                          <div style={{ opacity: 0.85 }}>{slot.start_uur}–{slot.eind_uur}</div>
                          {slot.heeft_conflict && <div className="text-xs">⚠️ Conflict</div>}
                        </div>
                      ))}
                      {isDoelCel && <div className="border-2 border-dashed border-blue-400 rounded h-8 flex items-center justify-center text-xs text-blue-500">Hier plaatsen</div>}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-4 mt-3 text-xs text-gray-500 flex-wrap">
        {Object.entries(STATUS_KLEUR).slice(0, 6).map(([s, c]) => (
          <span key={s} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c }} />{s}
          </span>
        ))}
      </div>

      {/* Bevestigingsdialoog */}
      {bevestig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-11/12 shadow-2xl">
            <h3 className="m-0 mb-3 text-[#1A3A5C] font-bold">📅 Slot verplaatsen?</h3>
            <p className="text-gray-700 mb-5">
              Order <strong>{bevestig.slot.order_nummer || '?'}</strong> verplaatsen naar{' '}
              <strong>{bevestig.resourceNaam}</strong> op <strong>{DAGEN[bevestig.nieuweDagIdx]}</strong>?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setBevestig(null)} className="px-4 py-2 border rounded-lg text-sm cursor-pointer hover:bg-gray-50">Annuleren</button>
              <button onClick={bevestigVerplaats} className="px-4 py-2 bg-[#1A3A5C] text-white rounded-lg text-sm font-bold cursor-pointer border-0">Bevestigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
